import { lazy, Suspense, useEffect, useState } from 'react';
import { LoadingScreen } from './components/Loading';
import { navigateReplace } from './lib/navigate';
import { Archive } from './routes/Archive';
import { CpuSetup } from './routes/CpuSetup';
import { Home } from './routes/Home';
import { LocalRoom } from './routes/LocalRoom';
import { Login } from './routes/Login';
import { Room } from './routes/Room';
import { Settings } from './routes/Settings';
import { Signup } from './routes/Signup';
import { Tutorial } from './routes/Tutorial';
import { useSession } from './state/session';

const MixLab = lazy(() => import('./routes/MixLab'));

const Puzzle = lazy(() => import('./routes/Puzzle'));

const Study = lazy(() => import('./routes/Study'));

interface Route {
  kind:
    | 'mix-lab'
    | 'home'
    | 'room'
    | 'archive'
    | 'study'
    | 'settings'
    | 'cpu-setup'
    | 'local-room'
    | 'signup'
    | 'tutorial'
    | 'puzzle'
    | 'unknown';
  code?: string;
  matchId?: string;
  studyId?: string;
  localId?: string;
  tutorialGame?: string;
}

function parseRoute(pathname: string): Route {
  if (pathname === '/experiments/mix') return { kind: 'mix-lab' };
  if (pathname === '/' || pathname === '') return { kind: 'home' };
  const r = pathname.match(/^\/r\/([A-Z2-9]{6})$/i);
  if (r) return { kind: 'room', code: r[1].toUpperCase() };
  const a = pathname.match(/^\/archive(?:\/([^/]+))?$/);
  if (a) return { kind: 'archive', matchId: a[1] };
  const st = pathname.match(/^\/study\/([a-z0-9]+)(?:\/\d+)?$/);
  if (st) return { kind: 'study', studyId: st[1] };
  if (pathname === '/settings') return { kind: 'settings' };
  if (pathname === '/signup') return { kind: 'signup' };
  if (pathname === '/local/new') return { kind: 'cpu-setup' };
  const lr = pathname.match(/^\/local\/([A-Za-z0-9_-]+)$/);
  if (lr) return { kind: 'local-room', localId: lr[1] };
  if (pathname === '/tutorial' || pathname === '/tutorial/') return { kind: 'tutorial' };
  const t = pathname.match(/^\/tutorial\/([a-z0-9-]+)$/);
  if (t) return { kind: 'tutorial', tutorialGame: t[1] };
  if (pathname === '/puzzle' || pathname === '/puzzle/') return { kind: 'puzzle' };
  return { kind: 'unknown' };
}

export function App() {
  const me = useSession((s) => s.me);
  const loading = useSession((s) => s.loading);
  const loadMe = useSession((s) => s.loadMe);
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));

  useEffect(() => {
    void loadMe();
    const onPop = () => setRoute(parseRoute(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [loadMe]);

  if (loading && !me) {
    return <LoadingScreen brand />;
  }

  if (!me) {
    // ゲスト許可ルート:
    //   - home / room: 招待リンクを直接踏んだケースを想定。 入室はゲストでも OK。
    //   - cpu-setup / local-room / tutorial / puzzle: 旧来のオフライン経路
    //   - settings: 設定は全部 localStorage (state/prefs.ts) なのでログイン不要
    if (
      route.kind === 'mix-lab' ||
      route.kind === 'study' ||
      route.kind === 'home' ||
      route.kind === 'room' ||
      route.kind === 'cpu-setup' ||
      route.kind === 'local-room' ||
      route.kind === 'tutorial' ||
      route.kind === 'puzzle' ||
      route.kind === 'settings'
    ) {
      // 続行
    } else {
      return <Login redirect={window.location.pathname + window.location.search} />;
    }
  }

  // 認証済みだが handle 未設定 → 強制で /signup に。 ローカル CPU / チュートリアル / puzzle / room は handle 不要。
  if (
    me &&
    !me.handle &&
    route.kind !== 'mix-lab' &&
    route.kind !== 'study' &&
    route.kind !== 'signup' &&
    route.kind !== 'cpu-setup' &&
    route.kind !== 'local-room' &&
    route.kind !== 'tutorial' &&
    route.kind !== 'puzzle' &&
    route.kind !== 'room' &&
    route.kind !== 'settings'
  ) {
    return <Signup />;
  }

  switch (route.kind) {
    case 'mix-lab':
      return <Suspense fallback={<LoadingScreen />}><MixLab /></Suspense>;
    case 'home':
      return <Home />;
    case 'room':
      return <Room code={route.code!} />;
    case 'archive':
      return <Archive matchId={route.matchId} />;
    case 'study':
      return <Suspense fallback={<LoadingScreen />}><Study id={route.studyId!} /></Suspense>;
    case 'settings':
      return <Settings />;
    case 'signup':
      return <Signup />;
    case 'cpu-setup':
      return <CpuSetup />;
    case 'local-room':
      return <LocalRoom id={route.localId!} />;
    case 'tutorial':
      return <Tutorial game={route.tutorialGame ?? null} />;
    case 'puzzle':
      return (
        <Suspense fallback={<LoadingScreen />}>
          <Puzzle />
        </Suspense>
      );
    case 'unknown':
      // 知らない URL は中間画面を見せず即 / に飛ばす。 OAuth state が KV から消えた
      // 時など、 backend が SPA の知らないパス (/login 等) に 302 してきたケースの
      // セーフティネット。
      navigateReplace('/');
      return null;
  }
}
